import React, { useEffect, useState } from "react";
import { apiGet } from "../../../utils/api_service";
import endpoint from "../../../config/endpoints";

export default function AttendanceSummary() {
  const [data, setData] = useState({
    checkedIn: 204,
    checkoutPending: 37,
    absent: 33,
    lateCheckin: 21,
  });

  const [teamData, setTeamData] = useState({
    total: 284,
    active: 251,
    inactive: 33,
    present: 218,
    onLeave: 19,
    newJoiners: 12,
    resigned: 5,
    notReporting: 14,
  });

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(false);

  /* -------------------------------------------------------------------------- */
  /*                               Fake API Delay                               */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    apiGet({
      url: endpoint.salesExecutive + "/get-sales-person-by-areas-optimize",
    }).then(({ data, success }) => {
      setLoading(false);
      console.log(data);
    });
  }, []);

  /* -------------------------------------------------------------------------- */
  /*                                   Loader                                   */
  /* -------------------------------------------------------------------------- */

  if (loading) {
    return <AttendanceSummarySkeleton />;
  }

  return (
    <>
      {/* Shimmer Animation */}

      <style>
        {`
          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }

            100% {
              background-position: 200% 0;
            }
          }
        `}
      </style>

      {/* Header */}

      <SectionHeader icon="🗓️" title="Attendance Summary" />

      {/* Layout */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
        {/* Cards */}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
          }}
        >
          <StatCard
            label="Check-In Done"
            value={data.checkedIn}
            sub="Logged in today"
            accent="green"
            icon="🟢"
          />

          <StatCard
            label="Checkout Pending"
            value={data.checkoutPending}
            sub="Still checked in"
            accent="amber"
            icon="🕐"
          />

          <StatCard
            label="Absent"
            value={data.absent}
            sub="No check-in"
            accent="red"
            icon="🔴"
          />

          <StatCard
            label="Late Check-In"
            value={data.lateCheckin}
            sub="After 9:30 AM"
            accent="amber"
            icon="⏰"
          />
        </div>

        {/* Progress Panel */}

        <div
          style={{
            background: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: 14,
            padding: "18px 20px",
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#94A3B8",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 14,
            }}
          >
            Today's Split
          </div>

          {[
            {
              label: "Checked In",
              val: data.checkedIn,
              pct: Math.round((data.checkedIn / teamData.present) * 100),
              color: "#10B981",
            },

            {
              label: "Checkout Pending",
              val: data.checkoutPending,
              pct: Math.round((data.checkoutPending / teamData.present) * 100),
              color: "#F59E0B",
            },

            {
              label: "Absent",
              val: data.absent,
              pct: Math.round((data.absent / teamData.total) * 100),
              color: "#EF4444",
            },

            {
              label: "Late Check-In",
              val: data.lateCheckin,
              pct: Math.round((data.lateCheckin / teamData.present) * 100),
              color: "#F97316",
            },
          ].map((item) => (
            <div
              key={item.label}
              style={{
                marginBottom: 14,
              }}
            >
              {/* Label */}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: "#475569",
                    fontWeight: 500,
                  }}
                >
                  {item.label}
                </span>

                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: item.color,
                  }}
                >
                  {item.val}{" "}
                  <span
                    style={{
                      color: "#94A3B8",
                      fontWeight: 400,
                    }}
                  >
                    ({item.pct}%)
                  </span>
                </span>
              </div>

              {/* Progress */}

              <AnimatedProgressBar pct={item.pct} color={item.color} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                                 Stat Card                                  */
/* -------------------------------------------------------------------------- */

function StatCard({ label, value, sub, accent, icon }) {
  const accents = {
    green: {
      bg: "#ECFDF5",
      text: "#059669",
      border: "#A7F3D0",
    },

    red: {
      bg: "#FEF2F2",
      text: "#DC2626",
      border: "#FECACA",
    },

    amber: {
      bg: "#FFFBEB",
      text: "#D97706",
      border: "#FDE68A",
    },

    blue: {
      bg: "#EFF6FF",
      text: "#2563EB",
      border: "#BFDBFE",
    },

    gray: {
      bg: "#F8FAFC",
      text: "#64748B",
      border: "#E2E8F0",
    },
  };

  const c = accents[accent] || accents.gray;

  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${c.border}`,
        borderRadius: 14,
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        transition: "transform 0.15s, box-shadow 0.15s",
        cursor: "default",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";

        e.currentTarget.style.boxShadow = `0 8px 24px ${c.border}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "none";

        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Header */}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#94A3B8",
          }}
        >
          {label}
        </span>

        <span
          style={{
            fontSize: 18,
            color: c.text,
          }}
        >
          {icon}
        </span>
      </div>

      {/* Value */}

      <span
        style={{
          fontSize: 28,
          fontWeight: 700,
          color: c.text,
          lineHeight: 1.1,
        }}
      >
        <AnimatedNumber value={value} />
      </span>

      {/* Sub */}

      {sub && (
        <span
          style={{
            fontSize: 11,
            color: "#94A3B8",
            fontWeight: 500,
          }}
        >
          {sub}
        </span>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Animated Number                               */
/* -------------------------------------------------------------------------- */

function AnimatedNumber({ value }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    let start = 0;

    const end = value;

    const duration = 900;

    const step = end / (duration / 16);

    const timer = setInterval(() => {
      start += step;

      if (start >= end) {
        setDisplay(end);

        clearInterval(timer);
      } else {
        setDisplay(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value]);

  return <>{display.toLocaleString()}</>;
}

/* -------------------------------------------------------------------------- */
/*                            Animated Progress Bar                           */
/* -------------------------------------------------------------------------- */

function AnimatedProgressBar({ pct, color }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setProgress(pct);
    }, 120);

    return () => clearTimeout(timer);
  }, [pct]);

  return (
    <div
      style={{
        height: 6,
        background: "#F1F5F9",
        borderRadius: 99,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${progress}%`,
          height: "100%",
          background: color,
          borderRadius: 99,
          transition: "width 1.2s cubic-bezier(0.4,0,0.2,1)",
          boxShadow: `0 0 10px ${color}55`,
        }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Section Header                              */
/* -------------------------------------------------------------------------- */

function SectionHeader({ icon, title }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        marginTop: 32,
        paddingBottom: 10,
        borderBottom: "1.5px solid #F1F5F9",
      }}
    >
      <span
        style={{
          fontSize: 20,
        }}
      >
        {icon}
      </span>

      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#475569",
        }}
      >
        {title}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Skeleton Components                             */
/* -------------------------------------------------------------------------- */

function AttendanceSummarySkeleton() {
  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }

            100% {
              background-position: 200% 0;
            }
          }
        `}
      </style>

      <SectionHeader icon="🗓️" title="Attendance Summary" />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
        {/* Cards */}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>

        {/* Progress Skeleton */}

        <div
          style={{
            background: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: 14,
            padding: "18px 20px",
          }}
        >
          <SkeletonLine width="40%" height={14} />

          <div style={{ marginTop: 24 }}>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  marginBottom: 18,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <SkeletonLine width="30%" height={12} />

                  <SkeletonLine width="18%" height={12} />
                </div>

                <SkeletonProgress />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function SkeletonCard() {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: 14,
        padding: "18px 20px",
      }}
    >
      <SkeletonLine width="45%" height={10} />

      <div style={{ marginTop: 16 }}>
        <SkeletonLine width="30%" height={30} />
      </div>

      <div style={{ marginTop: 16 }}>
        <SkeletonLine width="60%" height={10} />
      </div>
    </div>
  );
}

function SkeletonProgress() {
  return (
    <div
      style={{
        height: 6,
        background: "#E2E8F0",
        borderRadius: 99,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          width: "40%",
          height: "100%",
          background: "linear-gradient(90deg,#E2E8F0,#F8FAFC,#E2E8F0)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.5s infinite",
        }}
      />
    </div>
  );
}

function SkeletonLine({ width, height }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: 999,
        background: "linear-gradient(90deg,#E2E8F0,#F8FAFC,#E2E8F0)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }}
    />
  );
}
